/* ==========================================================================
   Tribune — theme.js
   Composants (Custom Elements) : en-tête, tiroirs, panier, formulaire produit,
   sélecteur de variante, galerie, filtres, recommandations, annonces, révélation.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};

  /* Utilitaires ---------------------------------------------------------- */
  const qs = (sel, root = document) => root.querySelector(sel);
  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, wait = 300) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };
  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');
  const fetchJSON = (url, opts = {}) => fetch(url, { headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, ...opts })
    .then((r) => r.json().then((data) => (r.ok ? data : Promise.reject(data))));

  let toastTimer;
  function toast(message) {
    const el = qs('#Toast');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
    el.style.animation = 'none'; void el.offsetWidth; el.style.animation = '';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => { el.hidden = true; }, 3200);
  }
  theme.toast = toast;

  /* Piège de focus pour tiroirs et modales */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"]), summary';
  let trapHandler = null;
  function trapFocus(container, first) {
    releaseFocus();
    const els = qsa(FOCUSABLE, container).filter((e) => e.offsetParent !== null || e === document.activeElement);
    (first || els[0] || container).focus({ preventScroll: true });
    trapHandler = (e) => {
      if (e.key !== 'Tab') return;
      const list = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
      if (!list.length) return;
      const firstEl = list[0], lastEl = list[list.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener('keydown', trapHandler);
  }
  function releaseFocus() { if (trapHandler) document.removeEventListener('keydown', trapHandler); trapHandler = null; }

  /* En-tête : hauteur, ombre au scroll, masquage ----------------------------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.header-wrapper') || this;
      this.setHeight = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
      this.onScroll = () => this.wrapper.classList.toggle('is-scrolled', window.scrollY > 4);
      this.setHeight();
      this.onScroll();
      this.ro = new ResizeObserver(this.setHeight);
      this.ro.observe(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
    disconnectedCallback() { this.ro && this.ro.disconnect(); window.removeEventListener('scroll', this.onScroll); }
  }
  customElements.define('sticky-header', StickyHeader);

  /* Menus déroulants : fermeture au clic extérieur / Échap */
  class DetailsDropdown extends HTMLElement {
    connectedCallback() {
      this.details = qs('details', this);
      this.onDocClick = (e) => { if (this.details.open && !this.contains(e.target)) this.close(); };
      this.onKey = (e) => { if (e.key === 'Escape' && this.details.open) { this.close(); qs('summary', this).focus(); } };
      document.addEventListener('click', this.onDocClick);
      this.addEventListener('keyup', this.onKey);
      if (this.dataset.hover === 'true' && window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', () => { clearTimeout(this.timer); this.closeOthers(); this.details.open = true; });
        this.addEventListener('mouseleave', () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.close(), 250); });
        /* Au survol le menu est déjà ouvert : un clic ne doit pas le refermer */
        qs('summary', this).addEventListener('click', (e) => {
          if (e.detail > 0 && this.details.open) e.preventDefault();
        });
      }
    }
    closeOthers() {
      qsa('details-dropdown').forEach((el) => { if (el !== this && el.close) { clearTimeout(el.timer); el.close(); } });
    }
    close() { this.details.open = false; }
    disconnectedCallback() { document.removeEventListener('click', this.onDocClick); }
  }
  customElements.define('details-dropdown', DetailsDropdown);

  /* Tiroir générique (menu mobile, filtres, panier) */
  class SideDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = qs('.drawer__panel', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keyup', (e) => { if (e.key === 'Escape') this.close(); });
      if (this.id) {
        document.addEventListener('click', (e) => {
          const opener = e.target.closest(`[aria-controls="${this.id}"]`);
          if (opener) { e.preventDefault(); this.open(opener); }
        });
      }
    }
    open(opener) {
      this.opener = opener || document.activeElement;
      this.hidden = false;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        document.body.classList.add('is-locked');
        if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
        trapFocus(this.panel);
      });
    }
    close() {
      this.classList.remove('is-open');
      document.body.classList.remove('is-locked');
      releaseFocus();
      if (this.opener && this.opener.setAttribute) { this.opener.setAttribute('aria-expanded', 'false'); this.opener.focus && this.opener.focus({ preventScroll: true }); }
    }
  }
  customElements.define('side-drawer', SideDrawer);

  /* Barre d'annonces défilante */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('.announcement__slide', this);
      if (this.slides.length < 2) return;
      this.index = 0;
      this.interval = parseInt(this.dataset.speed || '5', 10) * 1000;
      qsa('[data-dir]', this).forEach((b) => b.addEventListener('click', () => { this.go(this.index + parseInt(b.dataset.dir, 10)); this.restart(); }));
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
      this.addEventListener('focusin', () => this.stop());
      this.start();
      document.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.findIndex((s) => s.contains(e.target) || s === e.target);
        if (i > -1) { this.stop(); this.go(i); }
      });
    }
    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => s.setAttribute('aria-hidden', n === this.index ? 'false' : 'true'));
    }
    start() { if (this.dataset.autoplay !== 'true' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return; this.stop(); this.timer = setInterval(() => this.go(this.index + 1), this.interval); }
    stop() { clearInterval(this.timer); }
    restart() { this.stop(); this.start(); }
    disconnectedCallback() { this.stop(); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* Panier ------------------------------------------------------------------- */
  const cartSections = () => {
    const ids = new Set(['cart-icon-bubble']);
    if (qs('cart-drawer')) ids.add('cart-drawer');
    qsa('[data-cart-section]').forEach((el) => ids.add(el.dataset.cartSection));
    return Array.from(ids);
  };

  function renderCartSections(sections) {
    if (!sections) return;
    Object.entries(sections).forEach(([id, html]) => {
      if (!html) return;
      const doc = parseHTML(html);
      qsa(`[data-cart-render="${id}"]`).forEach((target) => {
        const source = qs(`[data-cart-render="${id}"]`, doc);
        if (source) target.innerHTML = source.innerHTML;
      });
    });
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) idInput.disabled = false;
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
    onSubmit(e) {
      e.preventDefault();
      const button = this.form.querySelector('[type="submit"]');
      if (!button || button.classList.contains('is-loading') || button.getAttribute('aria-disabled') === 'true') return;
      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');
      const errorBox = qs('[data-form-error]', this.closest('.product') || this);
      if (errorBox) errorBox.hidden = true;

      const body = new FormData(this.form);
      body.append('sections', cartSections().join(','));
      body.append('sections_url', window.location.pathname);

      fetch(`${routes.cartAdd}`, { method: 'POST', headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' }, body })
        .then((r) => r.json())
        .then((res) => {
          if (res.status) {
            if (errorBox) { errorBox.hidden = false; qs('[data-form-error-text]', errorBox).textContent = res.description || res.message; }
            else toast(res.description || res.message || strings.cartError);
            return;
          }
          if (theme.cartType === 'page' && !qs('cart-drawer')) {
            renderCartSections(res.sections);
            toast(strings.added);
            return;
          }
          renderCartSections(res.sections);
          const drawer = qs('cart-drawer');
          if (drawer) drawer.open(button); else toast(strings.added);
        })
        .catch(() => toast(strings.cartError))
        .finally(() => { button.classList.remove('is-loading'); button.removeAttribute('aria-disabled'); });
    }
  }
  customElements.define('product-form', ProductForm);

  class CartDrawer extends SideDrawer {
    connectedCallback() {
      super.connectedCallback();
      document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-open-cart]');
        if (link && theme.cartType === 'drawer') { e.preventDefault(); this.open(link); }
      });
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.onChange = debounce((input) => this.update(input.dataset.line, input.value), 350);
      this.addEventListener('change', (e) => { if (e.target.matches('[data-quantity-input]')) this.onChange(e.target); });
      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.update(remove.dataset.line, 0); }
      });
    }
    update(line, quantity) {
      const container = this.closest('[data-cart-container]') || this;
      container.classList.add('is-loading');
      const body = JSON.stringify({ line: parseInt(line, 10), quantity: parseInt(quantity, 10), sections: cartSections(), sections_url: window.location.pathname });
      fetchJSON(`${routes.cartChange}`, { method: 'POST', body })
        .then((res) => {
          renderCartSections(res.sections);
          const lineItem = res.items && res.items[line - 1];
          if (lineItem && parseInt(quantity, 10) > lineItem.quantity) toast(strings.quantityError.replace('[quantity]', lineItem.quantity));
        })
        .catch((err) => toast((err && (err.description || err.message)) || strings.cartError))
        .finally(() => { const c = this.closest('[data-cart-container]') || qs('[data-cart-container]'); if (c) c.classList.remove('is-loading'); container.classList.remove('is-loading'); });
    }
  }
  customElements.define('cart-items', CartItems);

  class CartNote extends HTMLElement {
    connectedCallback() {
      const input = qs('textarea', this);
      input.addEventListener('input', debounce(() => {
        fetchJSON(`${routes.cartUpdate}`, { method: 'POST', body: JSON.stringify({ note: input.value }) }).catch(() => {});
      }, 500));
    }
  }
  customElements.define('cart-note', CartNote);

  /* Champ quantité (+ / −) */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button[data-step]');
        if (!btn) return;
        e.preventDefault();
        const step = parseInt(this.input.step || '1', 10);
        const min = parseInt(this.input.min || '0', 10);
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const current = parseInt(this.input.value || '0', 10);
        const next = Math.min(max, Math.max(min, current + step * parseInt(btn.dataset.step, 10)));
        if (next !== current) { this.input.value = next; this.input.dispatchEvent(new Event('change', { bubbles: true })); }
        this.updateButtons();
      });
      this.input.addEventListener('change', () => this.updateButtons());
      this.updateButtons();
    }
    updateButtons() {
      const v = parseInt(this.input.value || '0', 10);
      const minus = qs('[data-step="-1"]', this), plus = qs('[data-step="1"]', this);
      if (minus) minus.disabled = v <= parseInt(this.input.min || '0', 10);
      if (plus) plus.disabled = this.input.max ? v >= parseInt(this.input.max, 10) : false;
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* Sélecteur de variante : recharge la section produit via l'API de rendu de section */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.url = this.dataset.url;
      this.addEventListener('change', (e) => this.onChange(e));
    }
    onChange(e) {
      const selected = qsa('input:checked, select', this).map((el) => el.tagName === 'SELECT' ? el.selectedOptions[0].dataset.valueId : el.dataset.valueId);
      if (selected.some((v) => !v)) return;
      const params = new URLSearchParams({ section_id: this.sectionId, option_values: selected.join(',') });
      const root = this.closest('[data-product-root]');
      if (root) root.classList.add('is-updating');
      this.abort && this.abort.abort();
      this.abort = new AbortController();
      fetch(`${this.url}?${params}`, { signal: this.abort.signal })
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          const newRoot = qs('[data-product-root]', doc);
          if (!root || !newRoot) return;
          qsa('[data-update]', newRoot).forEach((fresh) => {
            const current = qs(`[data-update="${fresh.dataset.update}"]`, root);
            if (current) current.replaceWith(fresh);
          });
          const variantId = newRoot.dataset.variantId;
          if (variantId) {
            root.dataset.variantId = variantId;
            if (this.dataset.updateUrl !== 'false') window.history.replaceState({}, '', `${this.url}?variant=${variantId}`);
            const mediaId = newRoot.dataset.featuredMedia;
            if (mediaId) document.dispatchEvent(new CustomEvent('variant:media', { detail: { sectionId: this.sectionId, mediaId } }));
          }
          const focusTarget = e.target.id && qs(`#${CSS.escape(e.target.id)}`, root);
          if (focusTarget) focusTarget.focus({ preventScroll: true });
        })
        .catch((err) => { if (err.name !== 'AbortError') toast(strings.cartError); })
        .finally(() => root && root.classList.remove('is-updating'));
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* Galerie produit : points sur mobile, image de la variante, visionneuse */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('.gallery', this);
      this.items = qsa('.gallery__item', this);
      this.dots = qsa('.gallery-thumbs button', this);
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.scrollToIndex(i)));
      if (this.track && this.dots.length) {
        this.track.addEventListener('scroll', debounce(() => {
          const w = this.items[0].offsetWidth;
          const i = Math.round(this.track.scrollLeft / (w || 1));
          this.setDot(i);
        }, 60), { passive: true });
      }
      this.onVariantMedia = (e) => {
        if (e.detail.sectionId !== this.dataset.section) return;
        const i = this.items.findIndex((it) => it.dataset.mediaId === String(e.detail.mediaId));
        if (i < 0) return;
        if (window.matchMedia('(max-width: 989px)').matches) this.scrollToIndex(i);
        else if (i > 0 && this.dataset.moveToFirst === 'true') { this.track.prepend(this.items[i]); this.items = qsa('.gallery__item', this); }
      };
      document.addEventListener('variant:media', this.onVariantMedia);
      this.addEventListener('click', (e) => {
        const zoom = e.target.closest('[data-zoom]');
        if (zoom && this.dataset.lightbox === 'true') { e.preventDefault(); this.openLightbox(zoom.dataset.zoom); }
        const play = e.target.closest('[data-play]');
        if (play) { const tpl = qs('template', play.parentElement); if (tpl) play.parentElement.replaceChildren(tpl.content.cloneNode(true)); }
      });
    }
    disconnectedCallback() { document.removeEventListener('variant:media', this.onVariantMedia); }
    scrollToIndex(i) { const it = this.items[i]; if (!it) return; this.track.scrollTo({ left: it.offsetLeft - this.track.offsetLeft - parseInt(getComputedStyle(this.track).paddingLeft, 10), behavior: 'smooth' }); this.setDot(i); }
    setDot(i) { this.dots.forEach((d, n) => d.setAttribute('aria-current', n === i ? 'true' : 'false')); }
    openLightbox(startId) {
      const tpl = qs('template[data-lightbox]', this);
      if (!tpl) return;
      const box = tpl.content.firstElementChild.cloneNode(true);
      document.body.appendChild(box);
      document.body.classList.add('is-locked');
      const close = () => { box.remove(); document.body.classList.remove('is-locked'); releaseFocus(); };
      qs('[data-lightbox-close]', box).addEventListener('click', close);
      box.addEventListener('keyup', (e) => { if (e.key === 'Escape') close(); });
      trapFocus(box, qs('[data-lightbox-close]', box));
      const start = qs(`[data-media-id="${startId}"]`, box);
      if (start) start.scrollIntoView({ block: 'start' });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* Barre d'achat fixe (mobile & desktop) */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const target = document.getElementById(this.dataset.target);
      if (!target) return;
      this.io = new IntersectionObserver(([entry]) => {
        const below = entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', !entry.isIntersecting && below);
      });
      this.io.observe(target);
      qs('button', this) && qs('button', this).addEventListener('click', () => {
        const btn = target.querySelector('[type="submit"]');
        if (btn) { btn.scrollIntoView({ behavior: 'smooth', block: 'center' }); btn.click(); }
      });
    }
    disconnectedCallback() { this.io && this.io.disconnect(); }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* Partage produit */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      qs('button', this).addEventListener('click', async () => {
        const data = { title: this.dataset.title, url: this.dataset.url };
        if (navigator.share) { try { await navigator.share(data); } catch (e) { /* annulé */ } return; }
        try { await navigator.clipboard.writeText(data.url); toast(this.dataset.copied); } catch (e) { window.prompt('', data.url); }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* Retrait en magasin */
  class PickupAvailability extends HTMLElement {
    connectedCallback() { this.load(); }
    load() {
      if (!this.dataset.variant) return;
      fetch(`${routes.root.replace(/\/$/, '')}/variants/${this.dataset.variant}/?section_id=pickup-availability`)
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          const content = qs('.shopify-section', doc) || doc.body;
          this.innerHTML = content.innerHTML;
        })
        .catch(() => { this.innerHTML = ''; });
    }
  }
  customElements.define('pickup-availability', PickupAvailability);

  /* Recommandations produit (chargées après la page) */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = () => fetch(this.dataset.url).then((r) => r.text()).then((html) => {
        const doc = parseHTML(html);
        const fresh = qs('product-recommendations', doc);
        if (fresh && fresh.innerHTML.trim().length) { this.innerHTML = fresh.innerHTML; this.classList.add('is-loaded'); }
        else this.closest('.shopify-section') && this.closest('.shopify-section').setAttribute('hidden', '');
      }).catch(() => {});
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => { if (entries[0].isIntersecting) { io.disconnect(); load(); } }, { rootMargin: '0px 0px 400px 0px' });
        io.observe(this);
      } else load();
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* Filtres et tri des collections / recherche */
  class FacetFiltersForm extends HTMLElement {
    connectedCallback() {
      this.onChange = debounce(this.submit.bind(this), 450);
      this.addEventListener('change', (e) => { if (e.target.closest('form')) this.onChange(e.target.closest('form')); });
      this.addEventListener('input', (e) => { if (e.target.matches('[data-price-input]')) this.onChange(e.target.closest('form')); });
      this.addEventListener('submit', (e) => { e.preventDefault(); this.submit(e.target); });
    }
    submit(form) {
      const params = new URLSearchParams(new FormData(form));
      qsa('[data-price-input]', form).forEach((i) => { if (!i.value) params.delete(i.name); });
      FacetFiltersForm.render(params.toString());
    }
    static render(query, push = true, scroll = false) {
      const activeId = document.activeElement && document.activeElement.id;
      const container = qs('[data-facets-section]');
      if (!container) return;
      const sectionId = container.dataset.facetsSection;
      const grid = qs('.product-grid-container');
      if (grid) grid.classList.add('is-loading');
      const url = `${window.location.pathname}?section_id=${sectionId}&${query}`;
      fetch(url).then((r) => r.text()).then((html) => {
        const doc = parseHTML(html);
        ['[data-facets-results]', '[data-facets-toolbar]', '[data-facets-active]', '[data-facets-sidebar]', '[data-facets-drawer-body]', '[data-facets-count]'].forEach((sel) => {
          const fresh = qs(sel, doc);
          qsa(sel).forEach((cur) => { if (fresh) cur.innerHTML = fresh.innerHTML; });
        });
        if (push) window.history.pushState({ facets: query }, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
        if (activeId) { const el = document.getElementById(activeId); if (el) el.focus({ preventScroll: true }); }
        if (scroll) { const top = qs('[data-facets-toolbar]'); if (top) window.scrollTo({ top: top.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height'), 10) || 0) - 16, behavior: 'smooth' }); }
      }).catch(() => toast(strings.cartError)).finally(() => { const g = qs('.product-grid-container'); if (g) g.classList.remove('is-loading'); });
    }
  }
  customElements.define('facet-filters-form', FacetFiltersForm);
  window.addEventListener('popstate', (e) => { if (qs('[data-facets-section]')) FacetFiltersForm.render(window.location.search.slice(1), false); });
  document.addEventListener('click', (e) => {
    const link = e.target.closest('[data-facet-link]');
    if (!link || !qs('[data-facets-section]')) return;
    e.preventDefault();
    const url = new URL(link.href, window.location.origin);
    FacetFiltersForm.render(url.searchParams.toString(), true, url.searchParams.has('page'));
  });

  /* Sélecteurs de pays / langue : envoi automatique */
  class LocalizationForm extends HTMLElement {
    connectedCallback() {
      qsa('select', this).forEach((s) => s.addEventListener('change', () => { const f = qs('form', this); f.requestSubmit ? f.requestSubmit() : f.submit(); }));
    }
  }
  customElements.define('localization-form', LocalizationForm);

  /* Vidéo différée (YouTube / Vimeo / fichier) */
  class DeferredMedia extends HTMLElement {
    connectedCallback() {
      const btn = qs('[data-play]', this);
      if (!btn) return;
      btn.addEventListener('click', () => {
        const tpl = qs('template', this);
        if (!tpl) return;
        const content = tpl.content.cloneNode(true);
        this.replaceChildren(content);
        const v = qs('video', this); if (v) v.play();
      });
    }
  }
  customElements.define('deferred-media', DeferredMedia);

  /* Apparition des sections au scroll */
  function initReveal(root = document) {
    if (!document.body.classList.contains('has-reveal') || !('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-revealed'); obs.unobserve(en.target); } });
    }, { rootMargin: '0px 0px -8% 0px' });
    qsa('.reveal:not(.is-revealed)', root).forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) el.classList.add('is-revealed');
      else io.observe(el);
    });
  }
  document.addEventListener('DOMContentLoaded', () => initReveal());
  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));

  /* Éditeur de thème : ouvrir le tiroir panier quand la section est sélectionnée */
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = qs('side-drawer', e.target) || qs('cart-drawer', e.target);
    if (drawer && drawer.open) drawer.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = qs('side-drawer', e.target) || qs('cart-drawer', e.target);
    if (drawer && drawer.close) drawer.close();
  });

  window.theme = theme;
})();


/* RATO : sommaires des politiques (Shopify retire les id des titres) */
(function () {
  function init() {
    var body = document.querySelector('.shopify-policy__body');
    if (!body) return;
    var norm = function (s) { return s.replace(/\s+/g, ' ').trim().replace(/^\d+\.?\s*/, '').toLowerCase(); };
    var heads = Array.prototype.slice.call(body.querySelectorAll('h2, h3'));
    Array.prototype.slice.call(body.querySelectorAll('a[href^="#"]')).forEach(function (a) {
      var id = a.getAttribute('href').slice(1);
      if (!id || document.getElementById(id)) return;
      var t = norm(a.textContent);
      for (var i = 0; i < heads.length; i++) {
        if (!heads[i].id && norm(heads[i].textContent) === t) {
          heads[i].id = id;
          heads[i].style.scrollMarginTop = '110px';
          break;
        }
      }
    });
    if (location.hash) {
      var el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
      if (el) el.scrollIntoView();
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
